import React from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Text,
  Avatar,
  makeStyles,
  Tooltip,
  mergeClasses,
  Spinner
} from '@fluentui/react-components'
import { ArrowUpload24Regular } from '@fluentui/react-icons'
import HoverMenu from './HoverMenu'
import rocheLogo from '../assets/roche.png'

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0px 9px',
    height: '45px',
    position: 'relative',
    background: '#022366'
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  title: {
    fontSize: '18px',
    fontWeight: '600'
  },
  // The main "AIScribe" tagline in the header.
  tagline: {
    position: 'absolute',
    left: '50px',
    top: '50%',
    transform: 'translate(0%, -50%)',
    pointerEvents: 'none',
    fontSize: '22px',
    fontWeight: '600',
    color: '#ffffff',
    transition: 'opacity 0.3s ease-in-out',
    '@media (max-width: 400px)': {
      opacity: 0
    }
  },
  authSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  userPill: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '2px',
    border: '1px solid #e0e0e0',
    borderRadius: '9999px',
    backgroundColor: '#ffffff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: '#f5f5f5'
    },
    // On wider screens, add back the padding for the logo
    '@media (min-width: 480px)': {
      padding: '4px 4px 4px 12px'
    }
  },
  logoImage: {
    height: '25px',
    width: '45px'
  },
  userPillName: {
    color: '#333333',
    fontWeight: '600',
    fontSize: '14px'
  },
  customAvatar: {
    backgroundColor: '#e4f5f1',
    color: '#005a46'
  },
  userTooltip: {
    color: '#ffffff',
    padding: '8px 12px',
    borderRadius: '4px',
    background: 'rgba(60, 64, 67, .9)',
    letterSpacing: '.8px',
    lineHeight: '16px',
    padding: '4px 8px',
    outline: '1px solid transparent',
    userSelect: 'text',
    zIndex: '1000'
  },
  sourceButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 10px',
    backgroundColor: '#f7f7f7'
  },
  naturalWidthButton: {
    minWidth: 'auto',
    width: '100%'
  },
  uploadActive: {
    backgroundColor: '#fdaa48',
    color: 'white',
    border: '1px solid #fdaa48',
    ':hover': {
      backgroundColor: '#E46D0D',
      border: '1px solid #E46D0D'
    }
  },
  llmActive: {
    backgroundColor: '#FFC300',
    color: '#1a1a1a',
    border: '1px solid #FFC300',
    ':hover': {
      backgroundColor: '#E6B000',
      border: '1px solid #E6B000'
    }
  },
  docLibActive: {
    backgroundColor: '#5090D3',
    color: 'white',
    border: '1px solid #5090D3',
    ':hover': {
      backgroundColor: '#4073A9',
      border: '1px solid #4073A9'
    }
  },
  roMineActive: {
    // backgroundColor: '#28A745', Green Color
    backgroundColor: '#e085fc', // Light Purple color
    color: 'white',
    // border: '1px solid #28A745',
    border: '1px solid #e085fc',
    // ':hover': {
    //   backgroundColor: '#218838', // Dark Green
    //   border: '1px solid #218838'
    // }
    ':hover': {
      backgroundColor: '#bc36f0', // Dark Purple
      border: '1px solid #bc36f0'
    }
  },
  sharedButtonStyles: {
    transition: 'all 0.15s ease-in-out',
    borderRadius: '10px',
    border: '1px solid #022366',
    color: '#022366',
    fontSize: '13px',
    padding: '4px'
  },
  uploadButton: {
    '&:hover': {
      border: '1px solid #fdaa48',
      color: '#fdaa48',
      backgroundColor: 'transparent'
    }
  },
  llmButton: {
    '&:hover': {
      border: '1px solid #FFC300',
      color: '#FFC300',
      backgroundColor: 'transparent'
    }
  },
  docLibButton: {
    '&:hover': {
      border: '1px solid #5090D3',
      color: '#5090D3',
      backgroundColor: 'transparent'
    }
  },
  roMineButton: {
    '&:hover': {
      // border: '1px solid #28A745', // dark green
      // color: '#28A745', // dark green
      border: '1px solid #e085fc', // light purple color
      color: '#e085fc', // light purple color
      backgroundColor: 'transparent'
    }
  }
})

/**
 * Header Component
 *
 * Renders the top navigation bar and settings controls for the application.
 * Features include:
 * 1. The application logo, tagline, and HoverMenu (for chat settings, prompt library).
 * 2. User authentication section (Sign in button or User Profile Pill).
 * 3. Data source selection buttons (Upload, DocLib, LLM, RoMine) which dictate the chat context.
 *
 * @param {Object} props - The component props.
 * @param {string} props.title - The title to display in the header.
 * @param {Function} props.onSourceChange - Callback triggered when a data source (DocLib, LLM, RoMine) is selected.
 * @param {string} props.activeButton - The currently active data source identifier.
 * @param {Object|null} props.user - The currently authenticated user object. Null if logged out.
 * @param {Function} props.onLogin - Callback to trigger the login flow.
 * @param {Function} props.onLogout - Callback to trigger the logout flow.
 * @param {Function} props.onUploadClick - Callback triggered when the Upload & Chat button is clicked.
 * @param {boolean} props.isUploadDisabled - Whether the Upload button should be disabled (e.g., during quota checks).
 * @param {Function} props.onClearChat - Callback to clear the current chat messages.
 * @param {Function} props.onShowInfo - Callback to display application information.
 * @param {Function} props.onShowPromptLibrary - Callback to open the Prompt Library window.
 * @param {boolean} props.isLoading - Global loading state; disables all source buttons when true.
 * @param {boolean} props.isUploading - Indicates if a file upload is currently in progress.
 */
const Header = ({
  title,
  onSourceChange,
  activeButton,
  user,
  onLogin,
  onLogout,
  onUploadClick,
  isUploadDisabled,
  onClearChat,
  onShowInfo,
  onShowPromptLibrary,
  isLoading,
  isUploading,
  docLibFilters,
  onDocLibApply,
    romineRequiredRole 
}) => {
  const styles = useStyles()

  /**
  * Prepares the content for the user profile tooltip.
  * If the user is logged in, it displays their display name and email.
  * If logged out, it evaluates to null.
  */
  const userTooltipContent = user
    ? <div>
      <Text block weight='semibold'>
        {user.displayName || 'User'}
      </Text>
      {/* Using a lighter color for the email makes it look cleaner */}
      <Text block size={200} style={{ color: '#d1d1d1' }}>
        {user.email}
      </Text>
    </div>
    : null // When no user, this is null and doesn't cause an error
  
    // Verify if the logged-in user possesses the developer role
    // const hasDeveloperRole = user?.roles?.includes('GLOAZUaiscribe-developers'); 
    // const hasDeveloperRole = user?.roles?.includes('GLOAZUaiscribe-medical-writers-cei');
    
    // UPDATE THIS: Verify if the logged-in user possesses the required dynamic role
    const hasDeveloperRole = user?.roles?.includes(romineRequiredRole || 'GLOAZUaiscribe-developers');

  return (
    <div className='container-fluid'>
      <div className='row'>
        <div className='col-12 p-0 pb-0'>
          <div className={styles.header}>
            <div className={styles.logoSection}>
              {user &&
                <HoverMenu
                  onClearChat={onClearChat}
                  onShowInfo={onShowInfo}
                  onLogout={onLogout}
                  onShowPromptLibrary={onShowPromptLibrary}
                  isLoading={isLoading}
                  isUploading={isUploading}
                />}
              <Text className={styles.title}>
                { }
              </Text>
            </div>
            {/* Add the new tagline */}
            {/*<Text as='div' className={styles.tagline}>
              AIScribe
            </Text>*/}
            <div className={styles.authSection}>
              {user
                ? <Tooltip
                  content={{
                    children: userTooltipContent,
                    className: styles.userTooltip
                  }}
                  relationship='label'
                  withArrow
                  positioning='below-end'
                >
                  <div className={styles.userPill}>
                    <img
                      src={rocheLogo}
                      alt='Persistent Logo'
                      className={styles.logoImage}
                    />

                    <Avatar
                      name={user.displayName || 'User'}
                      className={styles.customAvatar}
                    />
                  </div>
                </Tooltip>
                : <Button appearance='primary' size='small' onClick={onLogin}>
                  Sign in
                </Button>}
            </div>
          </div>

          {/* Source selection buttons - only show if user is logged in */}
          {user &&
            <div className={styles.sourceButtons}>
              <Tooltip content='Upload & Chat' relationship='label'>
                <Button
                  appearance={
                    activeButton === 'file_upload' ? 'primary' : 'outline'
                  }
                  className={mergeClasses(
                    styles.sharedButtonStyles,
                    styles.naturalWidthButton,
                    activeButton === 'file_upload'
                      ? styles.uploadActive
                      : styles.uploadButton
                  )}
                  onClick={onUploadClick}
                  disabled={isUploadDisabled || isUploading || isLoading}
                >
                  {isUploading
                    ? <Spinner size='tiny' />
                    : isUploadDisabled ? 'Checking...' : 'Upload'}
                </Button>
              </Tooltip>

              <Tooltip
                content="Response from Roche's in-house document repository"
                relationship='label'
              >
                <Button
                  appearance={activeButton === 'DocLib' ? 'primary' : 'outline'}
                  className={mergeClasses(
                    styles.sharedButtonStyles, // Base transition style
                    styles.naturalWidthButton,
                    activeButton === 'DocLib'
                      ? styles.docLibActive
                      : styles.docLibButton
                  )}
                  onClick={() => onSourceChange('DocLib')}
                  disabled={isUploading || isLoading}
                >
                  DocLib
                </Button>
              </Tooltip>

              <Tooltip
                content="Response from LLM's knowledge base"
                relationship='label'
              >
                <Button
                  appearance={activeButton === 'LLM' ? 'primary' : 'outline'}
                  className={mergeClasses(
                    styles.sharedButtonStyles,
                    styles.naturalWidthButton,
                    activeButton === 'LLM'
                      ? styles.llmActive
                      : styles.llmButton
                  )}
                  onClick={() => onSourceChange('LLM')}
                  disabled={isUploading || isLoading}
                >
                  LLM
                </Button>
              </Tooltip>

              <Tooltip
                //content='This source is unavailable'
                //relationship='label'
                  content={hasDeveloperRole ? 'Response from Romine' : 'This source is unavailable'}
                  relationship='label'
              >
                <Button
                  appearance={activeButton === 'RoMine' ? 'primary' : 'outline'}
                  className={mergeClasses(
                    styles.sharedButtonStyles,
                    styles.naturalWidthButton,
                    activeButton === 'RoMine'
                      ? styles.roMineActive
                      : styles.roMineButton
                  )}
                  onClick={() => onSourceChange('RoMine')}
                  //disabled
                  disabled={!hasDeveloperRole || isUploading || isLoading }
                >
                  RoMine
                </Button>
              </Tooltip>
            </div>}
        </div>
      </div>
    </div>
  )
}

Header.propTypes = {
  title: PropTypes.string,
  onSourceChange: PropTypes.func.isRequired,
  onUploadClick: PropTypes.func.isRequired,
  activeButton: PropTypes.string,
  user: PropTypes.object,
  onLogin: PropTypes.func,
  onLogout: PropTypes.func.isRequired,
  isUploadDisabled: PropTypes.bool,
  onClearChat: PropTypes.func.isRequired,
  onShowInfo: PropTypes.func.isRequired,
  onShowPromptLibrary: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isUploading: PropTypes.bool
}

Header.defaultProps = {
  isUploading: false
}

export default Header
